import React, { useRef, useEffect, useState, forwardRef } from "react";
import clsx from "clsx";
import { Button, Section, Text } from "@/components";
import { useApp } from "@/hooks";
import { CustomAccordion } from "./components";
import "./AskAnswerAct.scss";

const data = [
  {
    tabTitle: "Better Then ChatGPT",
    title: "Purpose-Built for Diligence\nNot Just Chat",
    description:
      "Generic AI tools weren’t made for investment workflows. IntelliRoom™ is built for serious diligence: it reads data rooms, parses Excel, flags risks, and cites its sources — all in a secure, investor-ready workspace.",
    icon: "/images/home/ai-head.svg",
    activeIcon: "/images/home/ai-head-hover.svg",
    contentImage: "/images/home/ask-answer-1.png",
  },
  {
    tabTitle: "DD Questions Generated",
    title: "DD Questions Generated.\nAnswers Delivered.",
    description:
      "Don’t know what questions to ask? We do. IntelliRoom™ builds your diligence checklist, delivers instant answers sourced from the data room and web, and flags the risks — so you know exactly where to focus your diligence.",
    icon: "/images/home/ai-checklist.svg",
    activeIcon: "/images/home/ai-checklist-hover.svg",
    contentImage: "/images/home/ask-answer-2.png",
  },
  {
    tabTitle: "A Private LLM Workspace",
    title: "A Private LLM Workspace\nfor Serious Investors",
    description:
      "IntelliRoom’s AI-powered workspace includes a private LLM, “AI Answers”, designed to help your team conduct follow-up research, draft memos, and while maintaining a shared source of truth across the diligence process.",
    icon: "/images/home/locked.svg",
    activeIcon: "/images/home/locked-hover.svg",
    contentImage: "/images/home/ask-answer-3.png",
  },
];

export const AskAnswerActSection = forwardRef<HTMLElement, {}>((props, ref: any) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  // useRef to keep track of the previous active tab without causing re-renders
  const prevActiveTabRef = useRef(0);
  // State to determine the slide direction for animations (changed to 'up' | 'down')
  const [slideDirection, setSlideDirection] = useState<'up' | 'down' | null>(null);
  const { doGetStarted } = useApp();
  // const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = ref?.current;
    if (!section) return;

    let intervalId: NodeJS.Timeout | null = null;

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsPlaying(true);
            // Start cycling tabs every 20 seconds when the section is in view
            intervalId = setInterval(() => {
              setActiveTab((prev) => {
                const nextTab = (prev + 1) % data.length;
                // Determine slide direction for auto-cycling: if nextTab is greater, slide up; otherwise, slide down
                setSlideDirection(nextTab > prev ? 'up' : 'up');
                prevActiveTabRef.current = prev; // Update previous tab reference
                return nextTab;
              });
            }, 1000 * 20); // Cycle every 20 seconds
          } else {
            // Stop cycling when out of view
            if (intervalId) clearInterval(intervalId);
            setIsPlaying(false);
            setActiveTab(0); // Reset to first tab when section is out of view
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of the section is visible
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
      if (intervalId) clearInterval(intervalId);
      setIsPlaying(false);
      setActiveTab(0); // Ensure reset on unmount
    };
  }, []);

  // Effect to update prevActiveTabRef and slideDirection when activeTab changes (manual clicks or auto-cycle)
  useEffect(() => {
    // Only update if the activeTab has actually changed
    if (activeTab !== prevActiveTabRef.current) {
      // Determine slide direction: if new tab index is greater, slide up; otherwise, slide down
      setSlideDirection('up');
      prevActiveTabRef.current = activeTab; // Update the reference to the current active tab
    }
  }, [activeTab]); // Runs whenever activeTab changes

  // Handler for tab clicks to update activeTab and slide direction
  const handleTabClick = (index: number) => {
    if (index !== activeTab) {
      // The useEffect above will handle setting slideDirection and prevActiveTabRef.current
      setActiveTab(index);
    }
  };

  return (
    <Section
      id="ask-answer-act-section"
      ref={ref}
      className="w-full mx-0 px-4 md:px-0 md:py-0 rounded-t-[20px] md:rounded-t-[60px] bg-white"
    >
      <div className="w-full md:py-20 md:pb-[180px] ">
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center gap-1">
            <Text.Heading size="lg" className="text-black">
              Ask
            </Text.Heading>

            <img
              src="/images/home/right-arrow.svg"
              className="w-6 h-6 md:w-10 md:h-10"
              alt="Arrow Right"
              loading="lazy" // Added lazy loading
            />

            <Text.Heading size="lg" className="text-black">
              Answer
            </Text.Heading>

            <img
              src="/images/home/right-arrow.svg"
              className="w-6 h-6 md:w-10 md:h-10"
              alt="Arrow Right"
              loading="lazy" // Added lazy loading
            />

            <Text.Heading size="lg" className="act-text">
              Act
            </Text.Heading>
          </div>

          <div className="max-w-[283px] md:max-w-[473px] justify-center text-center mt-1 md:mt-4">
            <Text.Description
              size="md"
              className="text-black font-400"
            >
              Instant answers from your entire data room — no searching, no
              sifting. Just ask.
            </Text.Description>
          </div>
        </div>

        {/* main content for desktop */}
        <div className="hidden lg:block mt-7 lg:px-25 flex justify-center">
          {/* tab */}
          <div className="ask-answer-act-tab flex flex-col items-center">
            {/* tab headers */}
            <div className="md:w-[calc(100%-40px)] lg:w-[1024px] tab-headers flex items-center justify-content border-b border-[#D5D5D5]">
              <div className="flex justify-center mx-auto lg:max-w-[921px]">
                {data.map((item, index) => (
                  <div
                    key={`tab-${index}`}
                    className={clsx(
                      "relative flex items-center gap-2.5 px-4 lg:px-7 py-4 cursor-pointer tab-item",
                      {
                        "active-tab": activeTab === index,
                      }
                    )}
                    onClick={() => handleTabClick(index)} // Use the new handleTabClick
                  >
                    <img
                      src={
                        activeTab === index
                          ? item.activeIcon
                          : item.icon
                      }
                      alt="tab icon"
                      className="w-8 h-8"
                      loading="lazy" // Added lazy loading
                    />
                    <Text.Description
                      size="sm"
                      className={`md:!text-[16px] lg:!text-[18px] md:leading-[26px] ${activeTab === index
                          ? "text-black font-500"
                          : "!text-[#555555]"
                        }`}
                    >
                      {item.tabTitle}
                    </Text.Description>

                    {activeTab === index && isPlaying && (
                      // Add key to force re-render and restart the progress animation
                      <div className="progress-bar" key={`progress-${activeTab}`}>
                        <div className="progress-fill"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* tab content */}
            {/* Add key={activeTab} to force re-render and re-trigger CSS animations on content change */}
            {/* Apply slide animation classes based on slideDirection state */}
            <div
              key={activeTab}
              className={clsx(
                "w-full tab-content flex self-center justify-center gap-5 mt-12 h-[500px] xl:h-[587px] mx-5 xl:mx-0",
                {
                  'slide-in-up': slideDirection === 'up', // Changed from 'slide-in-right'
                  'slide-in-down': slideDirection === 'down', // Changed from 'slide-in-left'
                }
              )}
            >
              <div className="max-w-[476px] min-w-[390px] h-full flex flex-col justify-between p-[40px] xl:p-[74px] bg-[#F9F9F9] rounded-[12px]">
                <div>
                  <Text.Description
                    size="xl"
                    className="text-black !font-semibold text-left md:!text-[26px]"
                  >
                    {data[activeTab].title}
                  </Text.Description>

                  <Text.Description
                    size="xs"
                    className="!text-[#383838] text-left mt-4"
                  >
                    {data[activeTab].description}
                  </Text.Description>
                </div>

                <div className="w-full">
                  <Button
                    className="w-full !border-none h-[60px]"
                    onClick={doGetStarted}
                  >
                    <span className="font-semibold text-base text-center md:text-[18px] md:leading-[26px] font-inter">
                      Get Started Free
                    </span>
                  </Button>

                  <Text.Description
                    size="sm"
                    className="text-black text-center mt-4 md:leading-[26px]"
                  >
                    14-day trial · No credit card required
                  </Text.Description>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center bg-[#F9F9F9] rounded-[12px] p-5 content-image-wrapper">
                <img
                  src={data[activeTab].contentImage}
                  alt="Content"
                  className="w-auto h-auto object-cover rounded-[12px]"
                  loading="lazy" // Added lazy loading
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* main content for mobile */}
      <div className="block lg:hidden w-full px-4 md:px-10 mt-6 mb-[71px]">
        <CustomAccordion
          items={data}
          activeTab={activeTab}
          setActiveTab={handleTabClick} // Use the new handleTabClick for accordion too
        />
      </div>
    </Section>
  );
});
